import React from "react";
import {
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubPopup,
  ContextMenuSubTrigger,
  EntitySelector,
  EntitySelectorEmpty,
  EntitySelectorInput,
  EntitySelectorPool,
  EntitySelectorSelected,
  Icon,
  MenuItem,
  MenuSeparator,
  MenuSub,
  MenuSubPopup,
  MenuSubTrigger,
  Text,
  type EntitySelectorConfig,
  type EntitySelection,
  type IconSize,
} from "@salestrak/ui";

import {
  STAGE_POOL_ID,
  WORKFLOW_POOL_ID,
  USER_POOL_ID,
  useIssueActions,
  type IssueActionRef,
} from "#app/hooks/kanban/use-issue-actions";
import { ENTITY_KIND_ICON } from "#app/lib/entity-visuals";
import type { Omittable } from "@salestrak/ui";

const EMPTY_SELECTIONS: EntitySelection[] = [];

/**
 * The menu parts the item set composes. Base UI ships a component per menu
 * kind (dropdown Menu vs right-click ContextMenu) with mirrored APIs, so the
 * shared item set takes the part components from its host instead of picking
 * a primitive itself.
 */
export type IssueMenuParts = Omittable<{
  Item: React.ComponentType<{
    onClick?: React.MouseEventHandler<HTMLElement>;
    disabled?: boolean;
    variant?: "default" | "destructive";
    children?: React.ReactNode;
  }>;
  Separator: React.ComponentType<object>;
  Sub: React.ComponentType<{ children?: React.ReactNode }>;
  SubTrigger: React.ComponentType<{
    disabled?: boolean;
    children?: React.ReactNode;
  }>;
  SubPopup: React.ComponentType<{
    children?: React.ReactNode;
    /** `none` lets full-bleed content (an entity selector) own its edges. */
    padding?: "none" | "2xs";
  }>;
}>;

export const DROPDOWN_MENU_PARTS: IssueMenuParts = {
  Item: MenuItem,
  Separator: MenuSeparator,
  Sub: MenuSub,
  SubTrigger: MenuSubTrigger,
  SubPopup: MenuSubPopup,
};

export const CONTEXT_MENU_PARTS: IssueMenuParts = {
  Item: ContextMenuItem,
  Separator: ContextMenuSeparator,
  Sub: ContextMenuSub,
  SubTrigger: ContextMenuSubTrigger,
  SubPopup: ContextMenuSubPopup,
};

type IssueMenuItemsProps = Omittable<{
  iconSize?: IconSize;
  /** Menu part set of the hosting menu kind. Defaults to the dropdown parts. */
  parts?: IssueMenuParts;
  isFavorited: boolean;
  isArchived: boolean;
  hasStages: boolean;
  hasOtherWorkflows: boolean;
  stageConfig: EntitySelectorConfig;
  stageValue: EntitySelection[];
  workflowConfig: EntitySelectorConfig;
  assigneeConfig: EntitySelectorConfig;
  assigneeSelections: EntitySelection[];
  handleStageValueChange: (selections: EntitySelection[]) => void;
  handleAssigneeValueChange: (selections: EntitySelection[]) => void;
  handleWorkflowValueChange: (selections: EntitySelection[]) => void;
  handleFavorite: () => void;
  handleCopyLink: () => void;
  handleArchive: () => void;
  // Opens the "Automation activity" dialog (every automation run that fired on
  // this issue). Sits under Copy link. Omitted → the row is hidden — the host
  // (IssueMenu) only passes it when automations are enabled + permitted, so
  // other menu consumers (favorites, table) don't show it.
  onAutomationRuns?: () => void;
  onRename: () => void;
  onDelete: () => void;
  // Opens the item Share dialog. Omit (or pass canManageSharing=false)
  // to hide the row — gating mirrors the workflow share action (only
  // viewers with `full_edit` on the item can manage its share list).
  onShare?: () => void;
  canManageSharing?: boolean;
  // Role-permission gates for the destructive footer. These OVERLAY
  // readOnly: a Guest with `delete.items` granted should still see
  // Delete even when their object access is view/comment, because the
  // backend allows the action.
  canDelete?: boolean;
  canArchive?: boolean;
  // When true, only the non-mutating items render (Favorite + Copy
  // link), plus any destructive action that `canDelete` / `canArchive`
  // explicitly enables.
  readOnly?: boolean;
}>;
export function renderIssueMenuItems({
  iconSize = "lg",
  parts = DROPDOWN_MENU_PARTS,
  isFavorited,
  isArchived,
  hasStages,
  hasOtherWorkflows,
  stageConfig,
  stageValue,
  workflowConfig,
  assigneeConfig,
  assigneeSelections,
  handleStageValueChange,
  handleAssigneeValueChange,
  handleWorkflowValueChange,
  handleFavorite,
  handleCopyLink,
  handleArchive,
  onAutomationRuns,
  onRename,
  onDelete,
  onShare,
  canManageSharing = false,
  canDelete = true,
  canArchive = true,
  readOnly = false,
}: IssueMenuItemsProps) {
  const { Item, Separator, Sub, SubTrigger, SubPopup } = parts;
  // The "Automation activity" row, rendered right under Copy link in both the
  // read-only and full menus. Only present when the host wired the handler.
  const automationRunsItem = onAutomationRuns ? (
    <Item onClick={onAutomationRuns}>
      <Icon name="activity" size={iconSize} color="muted" />
      <Text size="sm">Automation activity</Text>
    </Item>
  ) : null;
  if (readOnly) {
    const showArchive = canArchive;
    const showDelete = canDelete;
    return (
      <>
        <Item onClick={handleFavorite}>
          <Icon name="star" size={iconSize} color="muted" />
          <Text size="sm">{isFavorited ? "Unfavorite" : "Favorite"}</Text>
        </Item>
        <Item onClick={handleCopyLink}>
          <Icon name="link" size={iconSize} color="muted" />
          <Text size="sm">Copy link</Text>
        </Item>
        {automationRunsItem}
        {showArchive || showDelete ? <Separator /> : null}
        {showArchive ? (
          <Item onClick={handleArchive}>
            <Icon
              name={isArchived ? "archive-restore" : "archive"}
              size={iconSize}
              color="muted"
            />
            <Text size="sm">{isArchived ? "Unarchive" : "Archive"}</Text>
          </Item>
        ) : null}
        {showDelete ? (
          <Item variant="destructive" onClick={onDelete}>
            <Icon name="trash-2" size={iconSize} color="destructive" />
            <Text size="sm">Delete...</Text>
          </Item>
        ) : null}
      </>
    );
  }
  return (
    <>
      <Item onClick={onRename}>
        <Icon name="pencil" size={iconSize} color="muted" />
        <Text size="sm">Rename...</Text>
      </Item>

      <Separator />

      {hasStages && (
        <Sub>
          <SubTrigger>
            <Icon name="circle" size={iconSize} color="muted" />
            <Text size="sm">Stage</Text>
          </SubTrigger>
          <SubPopup padding="none">
            <EntitySelector
              variant="inline"
              config={stageConfig}
              value={stageValue}
              onValueChange={handleStageValueChange}
            >
              <EntitySelectorInput />
              <EntitySelectorPool value={STAGE_POOL_ID} />
              <EntitySelectorEmpty>No matching stages</EntitySelectorEmpty>
            </EntitySelector>
          </SubPopup>
        </Sub>
      )}

      <Sub>
        <SubTrigger>
          <Icon name={ENTITY_KIND_ICON.user} size={iconSize} color="muted" />
          <Text size="sm">Assignees</Text>
        </SubTrigger>
        <SubPopup padding="none">
          <EntitySelector
            variant="inline"
            config={assigneeConfig}
            value={assigneeSelections}
            onValueChange={handleAssigneeValueChange}
          >
            <EntitySelectorInput />
            <EntitySelectorSelected
              label="Assignees"
              hideWhenEmpty
              hideBadge
              hideSelectionHighlight
              maxHeight={160}
            />
            <EntitySelectorPool value={USER_POOL_ID} hideSelectionHighlight />
            <EntitySelectorEmpty>No members found</EntitySelectorEmpty>
          </EntitySelector>
        </SubPopup>
      </Sub>

      <Separator />

      <Sub>
        <SubTrigger disabled={!hasOtherWorkflows}>
          <Icon name={ENTITY_KIND_ICON.workflow} size={iconSize} color="muted" />
          <Text size="sm">Move to workflow...</Text>
        </SubTrigger>
        <SubPopup padding="none">
          <EntitySelector
            variant="inline"
            config={workflowConfig}
            value={EMPTY_SELECTIONS}
            onValueChange={handleWorkflowValueChange}
          >
            <EntitySelectorInput />
            <EntitySelectorPool value={WORKFLOW_POOL_ID} />
            <EntitySelectorEmpty>No other workflows</EntitySelectorEmpty>
          </EntitySelector>
        </SubPopup>
      </Sub>

      <Separator />

      <Item onClick={handleFavorite}>
        <Icon name="star" size={iconSize} color="muted" />
        <Text size="sm">{isFavorited ? "Unfavorite" : "Favorite"}</Text>
      </Item>

      <Item onClick={handleCopyLink}>
        <Icon name="link" size={iconSize} color="muted" />
        <Text size="sm">Copy link</Text>
      </Item>

      {automationRunsItem}

      {(canArchive || canDelete) && <Separator />}

      {canArchive ? (
        <Item onClick={handleArchive}>
          <Icon
            name={isArchived ? "archive-restore" : "archive"}
            size={iconSize}
            color="muted"
          />
          <Text size="sm">{isArchived ? "Unarchive" : "Archive"}</Text>
        </Item>
      ) : null}

      {canDelete ? (
        <Item variant="destructive" onClick={onDelete}>
          <Icon name="trash-2" size={iconSize} color="destructive" />
          <Text size="sm">Delete...</Text>
        </Item>
      ) : null}

      {canManageSharing && onShare ? (
        <>
          <Separator />
          <Item onClick={onShare}>
            <Icon name="lock" size={iconSize} color="muted" />
            <Text size="sm">Sharing and permissions</Text>
          </Item>
        </>
      ) : null}
    </>
  );
}

export function IssueMenuItemsWithActions({
  issue,
  iconSize,
  parts,
  prepend,
  onRename,
  onDelete,
  onShare,
  canManageSharing,
  canDelete,
  canArchive,
}: Omittable<{
  issue: IssueActionRef;
  iconSize?: IconSize;
  parts?: IssueMenuParts;
  prepend?: React.ReactNode;
  onRename: () => void;
  onDelete: () => void;
  onShare?: () => void;
  canManageSharing?: boolean;
  canDelete?: boolean;
  canArchive?: boolean;
}>) {
  const actions = useIssueActions(issue);
  return (
    <>
      {prepend}
      {renderIssueMenuItems({
        iconSize,
        parts,
        ...actions,
        onRename,
        onDelete,
        onShare,
        canManageSharing,
        canDelete,
        canArchive,
      })}
    </>
  );
}
